// src/lib/format.ts — display helpers. No pricing math lives here.

/** $349 for whole dollars, $331.55 otherwise (same rule as the customer pages). */
export function money(cents: number | null | undefined): string {
  if (cents === null || cents === undefined || !Number.isFinite(cents)) return "—";
  const whole = cents % 100 === 0;
  return (cents / 100).toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: whole ? 0 : 2,
    maximumFractionDigits: whole ? 0 : 2,
  });
}

/** Cents → the text an operator types ("339", "71.25"). */
export function toDollarText(cents: number | null | undefined): string {
  if (cents === null || cents === undefined) return "";
  return cents % 100 === 0 ? String(cents / 100) : (cents / 100).toFixed(2);
}

/** "339" / "$339.00" / "71.25" → cents; null when blank or not a valid amount. */
export function parseDollars(text: string): number | null {
  const t = text.replace(/[$,\s]/g, "");
  if (t === "" || !/^\d+(\.\d{1,2})?$/.test(t)) return null;
  return Math.round(parseFloat(t) * 100);
}

export function pct(n: number | null | undefined): string {
  return n === null || n === undefined ? "—" : `${Number(n).toFixed(1)}%`;
}

export const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

/** [4,5,6] → "Apr–Jun"; [11,12,1] → "Nov–Jan"; [3,5] → "Mar, May"; null → "All year". */
export function monthsLabel(months: number[] | null | undefined): string {
  if (!months || months.length === 0) return "All year";
  const m = Array.from(new Set(months)).filter((x) => x >= 1 && x <= 12).sort((a, b) => a - b);
  if (m.length === 12) return "All year";
  // Split into runs of consecutive months on a circular calendar.
  const runs: number[][] = [];
  for (const x of m) {
    const last = runs[runs.length - 1];
    if (last && x === last[last.length - 1] + 1) last.push(x);
    else runs.push([x]);
  }
  if (runs.length > 1 && runs[0][0] === 1 && runs[runs.length - 1].slice(-1)[0] === 12) {
    const tail = runs.pop() as number[];
    runs[0] = [...tail, ...runs[0]];
  }
  return runs
    .map((r) => (r.length === 1 ? MONTHS[r[0] - 1] : `${MONTHS[r[0] - 1]}–${MONTHS[r[r.length - 1] - 1]}`))
    .join(", ");
}

export type Tone = "good" | "ok" | "bad";

/** Sage above target, brass between floor and target, rust below floor or below cost. */
export function marginTone(
  marginPct: number | null | undefined,
  belowCost: boolean,
  target: number,
  floor: number,
): Tone | null {
  if (marginPct === null || marginPct === undefined) return null;
  if (belowCost || marginPct < floor) return "bad";
  if (marginPct > target) return "good";
  return "ok";
}

export function dateTime(iso: string): string {
  return new Date(iso).toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

/** One value from the change log, in words. */
export function logValue(key: string, v: unknown): string {
  if (v === null || v === undefined) {
    if (key === "available_months") return "All year";
    if (key === "presale_months") return "No pre-orders";
    if (key === "mobilization_cents") return "Global trip cost";
    if (key === "cart_visit_minimum_cents") return "No minimum";
    return "—";
  }
  if (key.endsWith("_cents") && typeof v === "number") return money(v);
  if ((key === "available_months" || key === "presale_months") && Array.isArray(v)) return monthsLabel(v as number[]);
  if (key.endsWith("_pct") && typeof v === "number") return `${v}%`;
  if (typeof v === "boolean") return v ? "Yes" : "No";
  if (Array.isArray(v)) return v.length ? v.join(", ") : "None";
  return String(v);
}

export const FIELD_LABEL: Record<string, string> = {
  base_price_cents: "Base price",
  obra_rate_cents: "Contractor rate (legacy)",
  estimated_hours: "Standard hours",
  seasonal_pricing: "Follows seasonal multipliers",
  active: "On sale",
  available_months: "Delivery months",
  presale_months: "Pre-order months",
  inactive_regions: "Switched off in",
  multiplier: "Multiplier",
  zip_price_factor: "ZIP factor",
  kit_cost_cents: "Kit",
  disposal_allowance_cents: "Disposal allowance",
  equipment_allowance_cents: "Equipment allowance",
  other_direct_cost_cents: "Other direct cost",
  mobilization_cents: "Trip cost",
  source: "Cost source",
  card_fee_pct: "Card fee %",
  card_fee_fixed_cents: "Card fee fixed",
  reference_zips: "Reference ZIPs",
  target_margin_pct: "Target margin",
  margin_floor_pct: "Margin floor",
  rate_titulo_aprendiz_cents: "Aprendiz rate",
  rate_titulo_oficial_cents: "Oficial rate",
  rate_titulo_maestro_cents: "Maestro rate",
  price_endings: "Price endings",
  install_hours: "Install hours",
  takedown_hours: "Takedown hours",
  crew_size: "Crew size",
  ladder_premium_cents_per_hr: "Ladder premium (per hour)",
  cart_visit_minimum_cents: "Visit minimum",
  job_status: "Status",
  assigned_contractor_id: "Maestro",
  final_rate_cents: "Maestro rate",
  scheduled_date: "Date",
  cascade_step: "Cascade step",
  step_started_at: "Step started",
  status: "Status",
  decision: "Decision",
  wave: "Wave",
  launch_date: "Launch date",
  notes: "Notes",
  label: "Area name",
  channel: "Contacted by",
};

/** "12 min", "5 h", "3 d" since an ISO time. */
export function waited(iso: string | null | undefined, now: number = Date.now()): string {
  if (!iso) return "—";
  const mins = Math.max(0, Math.round((now - new Date(iso).getTime()) / 60000));
  if (mins < 60) return `${mins} min`;
  const hours = Math.round(mins / 60);
  if (hours < 48) return `${hours} h`;
  return `${Math.round(hours / 24)} d`;
}

/** "Oct 20" for a YYYY-MM-DD date (no time-zone shift). */
export function shortDate(ymd: string | null | undefined): string {
  if (!ymd) return "—";
  const [y, m, d] = ymd.slice(0, 10).split("-").map(Number);
  if (!y || !m || !d) return ymd;
  return `${MONTHS[m - 1]} ${d}`;
}

/** Today in Pacific time as YYYY-MM-DD (the service area's calendar). */
export function todayPacific(): string {
  return new Intl.DateTimeFormat("en-CA", { timeZone: "America/Los_Angeles" }).format(new Date());
}

/** 88.9 → "88.9%"; null → "—". For values that are already percentages. */
export function pct1(n: number | string | null | undefined): string {
  if (n === null || n === undefined || n === "") return "—";
  const v = Number(n);
  return Number.isFinite(v) ? `${v.toFixed(1)}%` : "—";
}

/** 9964 → "9,964"; null → "—". */
export function count(n: number | string | null | undefined): string {
  if (n === null || n === undefined || n === "") return "—";
  const v = Number(n);
  return Number.isFinite(v) ? v.toLocaleString("en-US") : "—";
}

/** Whole-dollar amount from a plain dollar number (census incomes, home values). */
export function dollars(n: number | string | null | undefined): string {
  if (n === null || n === undefined || n === "") return "—";
  const v = Number(n);
  return Number.isFinite(v) ? v.toLocaleString("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }) : "—";
}

export type RateTone = "good" | "ok" | "bad";
/** Route scan rate bands (GE-04): sage over 2%, brass 1–2%, rust under 1%. */
export function scanRateTone(p: number | null | undefined): RateTone | null {
  if (p === null || p === undefined) return null;
  if (p > 2) return "good";
  if (p >= 1) return "ok";
  return "bad";
}
